import { useEffect } from "react";

import {
  type ProjectAction,
  type SelectOption,
  ProjectSetupType,
} from "../types";
import convertUrlToFile from "src/utils/convertUrlToFile";
import useGetAIWizardProjectSettings from "./useGetAIWizardFields";
import useGetTemplates from "./useGetTemplates";
import { Currency } from "src/hooks/useGetCurrencies";

interface UseSyncAIAttributesProps {
  projectAction: ProjectAction;
  setProjectName: (name: string) => void;
  setNewImage: (file: File) => void;
  setCurrentProjectSetupOption: (option: ProjectSetupType) => void;
  setSelectedBaseTemplateOption: (option: SelectOption | null) => void;
  setSelectedSocialPlatformTemplateOptions: (options: SelectOption[]) => void;
  setSelectedCurrencyOption: (option: string) => void;
  currencies: Currency[];
}

export function useSyncAIAttributes({
  projectAction,
  setProjectName,
  setNewImage,
  setCurrentProjectSetupOption,
  setSelectedBaseTemplateOption,
  setSelectedSocialPlatformTemplateOptions,
  setSelectedCurrencyOption,
  currencies,
}: UseSyncAIAttributesProps) {
  const {
    projectName,
    templates: aiTemplates,
    avatar,
    currencyCode,
  } = useGetAIWizardProjectSettings(projectAction);

  const {
    allSocialPlatformTemplateOptions,
    allBaseOptions,
    updatedProjectTemplates,
  } = useGetTemplates(aiTemplates);

  useEffect(() => {
    if (!projectName) return;
    setProjectName(projectName);
  }, [projectName, setProjectName]);

  useEffect(() => {
    if (!avatar) return;

    const handleConversion = async () => {
      const file = await convertUrlToFile(avatar, "avatar.jpg");
      if (file) {
        setNewImage(file);
      }
    };
    handleConversion();
  }, [setNewImage, avatar]);

  useEffect(() => {
    if (!aiTemplates.length) return;

    setCurrentProjectSetupOption(ProjectSetupType.Template);
    const foundBaseOptions = allBaseOptions.filter((option) =>
      aiTemplates.some(
        (aiTemplate) => aiTemplate.template_uuid === option.value,
      ),
    );

    if (foundBaseOptions.length) {
      setSelectedBaseTemplateOption(foundBaseOptions[0]);
    }

    const foundSocialPlatformOptions = allSocialPlatformTemplateOptions.filter(
      (option) =>
        aiTemplates.some(
          (aiTemplate) => aiTemplate.template_uuid === option.value,
        ),
    );

    if (foundSocialPlatformOptions.length) {
      setSelectedSocialPlatformTemplateOptions(foundSocialPlatformOptions);
    }
  }, [
    allBaseOptions,
    allSocialPlatformTemplateOptions,
    aiTemplates,
    setCurrentProjectSetupOption,
    setSelectedBaseTemplateOption,
    setSelectedSocialPlatformTemplateOptions,
  ]);

  useEffect(() => {
    if (!currencyCode) return;
    const foundCurrency = currencies.find(
      (currency) => currency.code === currencyCode,
    );
    if (!foundCurrency) return;
    setSelectedCurrencyOption(foundCurrency.code);
  }, [currencyCode, currencies, setSelectedCurrencyOption]);

  return {
    updatedProjectTemplates,
    allBaseOptions,
    allSocialPlatformTemplateOptions,
  };
}
